import { Route, matchRoutes } from 'react-router-dom';
import React from 'react';
import { lazyWithPreload, isPreloadable } from '../lazyWithPreload';
import { NewHomePage } from '../component/homepage/NewHomePage';
import PricingPage from '../component/pricing/PricingPage';
import { UseCasesPage } from '../component/use-cases/UseCasesPage';
import { PrivacyPage } from '../component/pages/PrivacyPage';
import { TermsPage } from '../component/pages/TermsPage';
import { ContactPage } from '../component/pages/ContactPage';
import { AboutPage } from '../component/pages/AboutPage';
import { SecurityPage } from '../component/pages/SecurityPage';
import { StatusPage } from '../component/pages/StatusPage';
import { BlogIndexPage } from '../component/blog/BlogIndexPage';
import { BlogArticlePage } from '../component/blog/BlogArticlePage';
import { IntegrationsIndexPage } from '../component/integrations/IntegrationsIndexPage';
import { IntegrationDetailPage } from '../component/integrations/IntegrationDetailPage';

/*
 * Code-split pages.
 *
 * These three are the only marketing pages that use Cloudscape. Importing them
 * eagerly pulled Cloudscape's ~275 KB of CSS and its JS into main.css/main.js, so
 * every visitor to the homepage downloaded and parsed it before anything could
 * paint — main.css is render-blocking.
 *
 * They are declared with lazyWithPreload rather than React.lazy because these
 * routes are prerendered: preloadMarketingRoute() primes the chunk before the app
 * renders, so the component resolves synchronously and hydration never hits a
 * Suspense boundary. The CSS is safe too — the prerendered HTML for these routes
 * already contains the <link> that mini-css-extract injected for the chunk, so it
 * is render-blocking on a real load exactly as main.css is.
 */
const DocsPage = lazyWithPreload(() =>
    import('../component/pages/DocsPage').then(m => ({ default: m.DocsPage }))
);
const DocsSectionPage = lazyWithPreload(() =>
    import('../component/pages/DocsPage').then(m => ({ default: m.DocsSectionPage }))
);
const McpPage = lazyWithPreload(() =>
    import('../component/pages/McpPage').then(m => ({ default: m.McpPage }))
);
const GetStarted = lazyWithPreload(() =>
    import('../component/GetStarted').then(m => ({ default: m.GetStarted }))
);

type MarketingRoute = {
    /** Path relative to the enclosing tree. Omitted for the index route. */
    path?: string;
    Component: React.ComponentType;
};

/**
 * Public marketing/content routes, shared by both route trees.
 *
 * These pages are reachable whether or not a user is signed in, so they must be
 * registered in PublicRoutes (anonymous visitors) *and* PrivateRoutes (signed-in
 * users) — NavigationComponent picks one tree or the other based on auth state.
 * Keeping them in one place means a new marketing page cannot render for
 * anonymous visitors but come up blank for signed-in ones.
 *
 * Held as data rather than JSX so the same list drives both the <Route> elements
 * and the preload lookup below; a second hand-maintained path list would drift and
 * silently cost a route its prerender.
 *
 * Paths are relative so the fragment can be nested (PublicRoutes mounts it under
 * LocaleLayout for the /:lang prefix) or used at the top level (PrivateRoutes).
 */
export const MARKETING_ROUTES: readonly MarketingRoute[] = [
    { Component: NewHomePage },
    { path: 'pricing', Component: PricingPage },
    { path: 'get-started', Component: GetStarted },
    { path: 'use-cases', Component: UseCasesPage },
    { path: 'privacy', Component: PrivacyPage },
    { path: 'terms', Component: TermsPage },
    { path: 'contact', Component: ContactPage },
    { path: 'about', Component: AboutPage },
    { path: 'security', Component: SecurityPage },
    { path: 'status', Component: StatusPage },
    { path: 'docs', Component: DocsPage },
    /*
     * One URL per documentation section. Parameterised rather than eight literal
     * routes because the slugs live in src/component/docs/docsSections.json, which
     * scripts/seo-routes.js reads to prerender them and scripts/generate-llms.js
     * reads to link them — spelling them out again here is exactly the kind of
     * duplicated list that has already rotted twice in this codebase.
     */
    { path: 'docs/:section', Component: DocsSectionPage },
    { path: 'mcp', Component: McpPage },
    { path: 'blog', Component: BlogIndexPage },
    { path: 'blog/:slug', Component: BlogArticlePage },
    { path: 'integrations', Component: IntegrationsIndexPage },
    { path: 'integrations/:slug', Component: IntegrationDetailPage },
];

export function marketingRoutes() {
    return (
        <>
            {MARKETING_ROUTES.map(({ path, Component }) =>
                path === undefined
                    ? <Route key="index" index element={<Component/>} />
                    : <Route key={path} path={path} element={<Component/>} />
            )}
        </>
    );
}

/** Locale prefixes PublicRoutes mounts the marketing tree under, plus the bare root. */
const PRELOAD_MATCH_TREE = [
    {
        path: '/',
        children: MARKETING_ROUTES.map(({ path, Component }) =>
            path === undefined ? { index: true, Component } : { path, Component }
        ),
    },
    {
        path: '/:lang',
        children: MARKETING_ROUTES.map(({ path, Component }) =>
            path === undefined ? { index: true, Component } : { path, Component }
        ),
    },
];

/**
 * Loads the chunk for whichever marketing route `pathname` resolves to.
 *
 * Called from src/index.tsx before the app renders. Resolves immediately for eager
 * pages and for paths that are not marketing routes at all (dashboard URLs), so it
 * is safe to await unconditionally.
 *
 * Matching goes through react-router's own `matchRoutes` rather than string
 * comparison so that parameterised paths (blog/:slug, integrations/:slug) and the
 * locale prefix resolve exactly the way the router will resolve them at render
 * time.
 */
export function preloadMarketingRoute(pathname: string): Promise<unknown> {
    const matches = matchRoutes(PRELOAD_MATCH_TREE, pathname) ?? [];
    const pending = matches
        .map(match => (match.route as { Component?: unknown }).Component)
        .filter(isPreloadable)
        .map(component => component.preload());
    return Promise.all(pending);
}
